import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BidDecisionInput, BidListQuery, SubmitBidInput } from '@teentack/shared';
import { assignmentKeys } from '@/features/assignments/use-assignments';
import { jobKeys } from '@/features/jobs/use-jobs';
import { decideBid, fetchJobBids, fetchMyBids, submitBid, withdrawBid } from './bids.api';

export const bidKeys = {
  all: ['bids'] as const,
  mine: (query: Partial<BidListQuery>) => ['bids', 'mine', query] as const,
  forJobs: (query: Partial<BidListQuery>) => ['bids', 'poster', query] as const,
};

export function useMyBids(query: Partial<BidListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: bidKeys.mine(query),
    queryFn: () => fetchMyBids(query),
    enabled,
  });
}

export function useJobBids(query: Partial<BidListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: bidKeys.forJobs(query),
    queryFn: () => fetchJobBids(query),
    enabled,
  });
}

/** Accepting a bid creates an assignment, so that cache is stale too. */
function invalidateAll(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: bidKeys.all });
  void queryClient.invalidateQueries({ queryKey: jobKeys.all });
  void queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['poster'] });
  void queryClient.invalidateQueries({ queryKey: ['teen'] });
}

export function useSubmitBid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitBidInput) => submitBid(input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useWithdrawBid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bidId: string) => withdrawBid(bidId),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useDecideBid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bidId, ...input }: BidDecisionInput & { bidId: string }) =>
      decideBid(bidId, input),
    onSuccess: () => invalidateAll(queryClient),
  });
}
